// apps/client/src/ui-kit/ReferralDashboard.tsx
//
// Referral Dashboard - Glassmorphism panel for referral stats
// Features: Copy code, share button, earnings display
//

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './Button';
import Icon from './Icon';

interface ReferralDashboardProps {
    code: string | null;
    shareUrl: string | null;
    count: number;
    earnings: number;
    onShare?: () => void;
}

export function ReferralDashboard({
    code,
    shareUrl,
    count,
    earnings,
    onShare
}: ReferralDashboardProps) {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        if (!code) return;

        try {
            await navigator.clipboard.writeText(shareUrl || code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            console.log('📋 Referral code copied:', code);
        } catch (err) {
            console.error('Copy failed:', err);
        }
    };

    const handleShare = () => {
        if (onShare) {
            onShare();
        } else if (shareUrl && code) {
            const text = `Join BingoTon and earn free BingoChips! 🎯 Use my code: ${code}`;
            const url = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(text)}`;
            window.open(url, '_blank');
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative overflow-hidden"
        >
            {/* Glassmorphism container */}
            <div className="relative bg-gradient-to-br from-purple-600/30 via-pink-500/20 to-amber-500/10 backdrop-blur-xl border border-purple-400/30 rounded-3xl p-6">
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#FFC107]/20 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-purple-500/20 rounded-full blur-2xl" />

                {/* Content */}
                <div className="relative z-10">
                    {/* Header */}
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#FFC107] to-[#E88A00] flex items-center justify-center">
                            <Icon name="group_add" className="text-2xl text-[#1a0b2e]" />
                        </div>
                        <div>
                            <h3 className="text-white font-black text-lg">Invite & Earn</h3>
                            <p className="text-purple-300 text-xs">100 BingoChips per friend</p>
                        </div>
                    </div>

                    {/* Code Badge */}
                    <div className="bg-black/30 rounded-2xl p-4 mb-4">
                        <p className="text-[10px] text-purple-300 uppercase tracking-widest mb-2">Tu código de referido</p>
                        <div className="flex items-center justify-between">
                            <motion.span
                                key={code}
                                initial={{ scale: 0.9 }}
                                animate={{ scale: 1 }}
                                className="text-2xl font-black text-[#FFC107] tracking-[0.2em] font-mono"
                            >
                                {code ?? '------'}
                            </motion.span>
                            <button
                                onClick={handleCopy}
                                className={`p-3 rounded-xl transition-all ${copied
                                    ? 'bg-green-500/30 text-green-400'
                                    : 'bg-white/10 text-white hover:bg-white/20'
                                    }`}
                            >
                                <Icon name={copied ? 'check' : 'content_copy'} />
                            </button>
                        </div>
                    </div>

                    {/* Stats Row */}
                    <div className="grid grid-cols-2 gap-4 mb-6">
                        <div className="bg-white/5 rounded-xl p-4 text-center">
                            <motion.div
                                key={count}
                                initial={{ scale: 1.2 }}
                                animate={{ scale: 1 }}
                                className="text-3xl font-black text-white"
                            >
                                {count}
                            </motion.div>
                            <div className="text-[10px] text-gray-400 uppercase tracking-wider">Friends Invited</div>
                        </div>
                        <div className="bg-white/5 rounded-xl p-4 text-center">
                            <motion.div
                                key={earnings}
                                initial={{ scale: 1.2 }}
                                animate={{ scale: 1 }}
                                className="text-3xl font-black text-[#FFC107]"
                            >
                                {earnings.toLocaleString()}
                            </motion.div>
                            <div className="text-[10px] text-gray-400 uppercase tracking-wider">BingoChips Earned</div>
                        </div>
                    </div>

                    {/* Share Button */}
                    <Button
                        onClick={handleShare}
                        fullWidth
                        icon="share"
                        disabled={!code}
                    >
                        Compartir en Telegram
                    </Button>
                </div>
            </div>
        </motion.div>
    );
}
